import { DeviceFamily } from '@core/referential/device-family.enum'
import { Frequency } from '@core/referential/frequency.enum'
import { UnitType } from '@core/referential/unit-type.enum'
import { UsageType } from '@core/referential/usage-type.enum'
import { UseCase } from '@core/referential/use-case.enum'
import { AllowedFrequencies } from '@shared/schema/device.data-energy.schema'
import { createFileRoute, redirect } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { z } from 'zod'

import PendingComponent from '@/components/pending-component'
import { useAuth } from '@/hooks/use-auth'
import { parseDateRange } from '@/lib/date'
import { isUseCase } from '@/lib/utils'
import { companyDevicesQuery } from '@/queries/company.devices.query'

import { filterDeviceFamilies, filterDevices, filterSites, getAvailableUnits } from '../-utils/utils'

export const defaultDateRange = parseDateRange({
  startDate: dayjs().startOf('month').subtract(1, 'month').toDate(),
  endDate: dayjs().endOf('month').toDate(),
})

export const SearchSchema = z.object({
  startDate: z.string().default(defaultDateRange.startDate),
  endDate: z.string().default(defaultDateRange.endDate),
  tab: z.union([z.literal('meter'), z.literal('sub-meter')]).default('meter'),
  energy: z.nativeEnum(DeviceFamily).optional(),
  frequency: AllowedFrequencies.default(Frequency.MONTHLY),
  site: z.string().optional(),
  devices: z.array(z.string()).optional(),
})

export type Search = z.infer<typeof SearchSchema>

export const Route = createFileRoute('/_dashboard/energy-usage/meter-analysis/')({
  validateSearch: SearchSchema,
  async beforeLoad({ search, context: { queryClient } }) {
    if (!isUseCase(UseCase.CONSUMPTIONS)) {
      throw redirect({
        to: '/403',
        search: { reason: 'wrongUseCase' },
      })
    }

    const { setCurrentUsageType, setCurrentUnitType, setAvailableUnits, user } = useAuth.getState()
    const usageType = user?.currentUsageType ?? UsageType.ENERGY
    const unitType = user?.currentUnitType ?? UnitType.MWH

    if (!user?.currentUsageType) {
      setCurrentUsageType(usageType)
    }

    const companyDevices = await queryClient.ensureQueryData(companyDevicesQuery())

    if (companyDevices.length === 0) {
      return
    }

    const availableDevices = filterDevices({ devices: companyDevices, meterType: search.tab, usageType })
    const availableEnergy = filterDeviceFamilies({ devices: availableDevices, usageType })
    const availableSites = filterSites({ devices: availableDevices })
    const site = availableSites.some(({ id }) => id === search.site) ? search.site : availableSites[0]?.id

    if ((!search.energy && availableEnergy.length > 0) || (search.energy && !availableEnergy.includes(search.energy))) {
      const energy = availableEnergy[0]
      const availableUnits = getAvailableUnits(energy)
      const unit = availableUnits.includes(unitType) ? unitType : availableUnits[0]

      setCurrentUnitType(unit)
      setAvailableUnits({ [usageType]: availableUnits })
      throw redirect({
        to: '/energy-usage/meter-analysis',
        search: { ...search, energy, site },
        throw: false,
      })
    } else if (!search.site && site) {
      throw redirect({
        to: '/energy-usage/meter-analysis',
        search: { ...search, site },
        throw: false,
      })
    }
  },
  pendingComponent: PendingComponent,
})
